"use client";

import { SourceDocumentUIPart, SourceUrlUIPart } from "ai";
import { createContext, useContext } from "react";
import type { Root, ElementContent, Element as HastElement } from "hast";
import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationCarouselItem,
  InlineCitationCarouselNext,
  InlineCitationCarouselPrev,
  InlineCitationSource,
} from "../ai-elements/inline-citation";
import { WebReferenceMetadata, DocumentReferenceMetadata } from "@/generated";
import { mergeContiguousSameUrlSources, type GroupedPart } from "./utils";

// ---------------------------------------------------------------------------
// Marker pattern — injected into markdown text, transformed by rehype plugin
// ---------------------------------------------------------------------------

const CITE_MARKER_RE = /%%CITE:(\d+)%%/g;

/**
 * Append a citation marker to a markdown string.
 * Returns the marker string for a given citation index.
 */
export function citationMarker(index: number): string {
  return `%%CITE:${index}%%`;
}

/**
 * If the animated text slice ends in the middle of a %%CITE:N%% marker,
 * snap forward to include the full marker so it renders atomically.
 */
export function snapPastMarker(partial: string, full: string): string {
  const markerStart = partial.lastIndexOf("%%CITE:");
  if (markerStart === -1) return partial;
  const markerEnd = full.indexOf("%%", markerStart + 7);
  if (markerEnd === -1) return partial;
  // If partial ends inside the marker, snap to include the full marker
  if (partial.length > markerStart && partial.length < markerEnd + 2) {
    return full.slice(0, markerEnd + 2);
  }
  return partial;
}

// ---------------------------------------------------------------------------
// Rehype plugin — transforms %%CITE:N%% text into <cite> HAST elements
// ---------------------------------------------------------------------------

function walkAndReplace(node: Root | HastElement): void {
  if (!("children" in node)) return;

  for (let i = 0; i < node.children.length; i++) {
    const child = node.children[i];

    if (child.type === "text") {
      CITE_MARKER_RE.lastIndex = 0;
      if (!CITE_MARKER_RE.test(child.value)) continue;

      // Split text node around markers
      CITE_MARKER_RE.lastIndex = 0;
      const replacement: ElementContent[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = CITE_MARKER_RE.exec(child.value)) !== null) {
        if (match.index > lastIndex) {
          replacement.push({
            type: "text",
            value: child.value.slice(lastIndex, match.index),
          });
        }
        replacement.push({
          type: "element",
          tagName: "cite",
          properties: { dataCiteIndex: match[1] },
          children: [],
        });
        lastIndex = CITE_MARKER_RE.lastIndex;
      }

      if (lastIndex < child.value.length) {
        replacement.push({
          type: "text",
          value: child.value.slice(lastIndex),
        });
      }

      // Splice replacement nodes in place of the original text node
      node.children.splice(i, 1, ...replacement);
      i += replacement.length - 1; // skip past inserted nodes
      continue;
    }

    // Recurse into element children
    if (child.type === "element") {
      walkAndReplace(child);
    }
  }
}

export function rehypeCitationMarkers() {
  return (tree: Root) => {
    walkAndReplace(tree);
  };
}

// ---------------------------------------------------------------------------
// React context for passing citation source data to the <cite> component
// ---------------------------------------------------------------------------

type CitationStyle = "text" | "numbered" | "hidden";

interface CitationContextValue {
  sources: (SourceUrlUIPart | SourceDocumentUIPart)[][];
  citationStyle: CitationStyle;
  omitCitationQuotes: boolean;
}

export const CitationSourcesContext = createContext<CitationContextValue>({
  sources: [],
  citationStyle: "text",
  omitCitationQuotes: false,
});

// ---------------------------------------------------------------------------
// getCitationLabel — extracted from NoxusMessageContent for reuse
// ---------------------------------------------------------------------------

const MAX_CHIP_LENGTH = 30;

function getRefNum(
  source: SourceUrlUIPart | SourceDocumentUIPart
): number | undefined {
  return (source.providerMetadata?.noxus as { ref_num?: number } | undefined)
    ?.ref_num;
}

function getCitationLabel(
  sources: (SourceUrlUIPart | SourceDocumentUIPart)[],
  citationStyle: "text" | "numbered"
): string {
  if (citationStyle === "numbered") {
    const nums = sources.map((s) => getRefNum(s)).filter((n) => n != null);
    if (nums.length === 0) return "?";
    const unique = [...new Set(nums)];
    return unique.join(", ");
  }
  const first = sources[0];
  if (!first) return "unknown";
  let text: string;
  if (first.type === "source-url") {
    text =
      first.title && first.title !== first.url
        ? first.title
        : (() => {
            try {
              return new URL(first.url).hostname;
            } catch {
              return first.url;
            }
          })();
  } else {
    const raw = first.title || first.filename || "Document";

    let decodedText: string;
    try {
      decodedText = raw.includes("%") ? decodeURIComponent(raw) : raw;
    } catch (error) {
      decodedText = raw;
    }
    text = decodedText;
  }
  if (text.length > MAX_CHIP_LENGTH) {
    text = text.slice(0, MAX_CHIP_LENGTH - 1) + "\u2026";
  }
  if (sources.length > 1) text += ` +${sources.length - 1}`;
  return text;
}

// ---------------------------------------------------------------------------
// CitationComponent — rendered by Streamdown for <cite> elements
// ---------------------------------------------------------------------------

interface CitationComponentProps {
  "data-cite-index"?: string;
  node?: HastElement;
}

export function CitationComponent(props: CitationComponentProps) {
  const { sources, citationStyle, omitCitationQuotes } = useContext(
    CitationSourcesContext
  );
  if (citationStyle === "hidden") return null;
  const idx = Number(props["data-cite-index"]);
  const group = sources[idx];
  if (!group || group.length === 0) return null;

  const dedupedSources = omitCitationQuotes
    ? mergeContiguousSameUrlSources(group)
    : group;

  return (
    <CitationElement
      sources={dedupedSources}
      citationStyle={citationStyle}
      omitCitationQuotes={omitCitationQuotes}
    />
  );
}

// ---------------------------------------------------------------------------
// StandaloneCitation — for source-groups that have no preceding text part
// ---------------------------------------------------------------------------

export function StandaloneCitation({
  sources,
  citationStyle,
  omitCitationQuotes,
}: {
  sources: (SourceUrlUIPart | SourceDocumentUIPart)[];
  citationStyle: "text" | "numbered";
  omitCitationQuotes: boolean;
}) {
  if (sources.length === 0) return null;
  const dedupedSources = omitCitationQuotes
    ? mergeContiguousSameUrlSources(sources)
    : sources;
  return (
    <span className="leading-relaxed">
      <CitationElement
        sources={dedupedSources}
        citationStyle={citationStyle}
        omitCitationQuotes={omitCitationQuotes}
      />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Shared citation element builder
// ---------------------------------------------------------------------------

function CitationElement({
  sources,
  citationStyle,
  omitCitationQuotes = false,
}: {
  sources: (SourceUrlUIPart | SourceDocumentUIPart)[];
  citationStyle: "text" | "numbered";
  omitCitationQuotes?: boolean;
}) {
  return (
    <InlineCitation>
      <InlineCitationCard>
        <InlineCitationCardTrigger
          label={getCitationLabel(sources, citationStyle)}
        />
        <InlineCitationCardBody>
          <InlineCitationCarousel>
            <InlineCitationCarouselHeader>
              <InlineCitationCarouselPrev />
              <InlineCitationCarouselNext />
              <InlineCitationCarouselIndex />
            </InlineCitationCarouselHeader>
            <InlineCitationCarouselContent>
              {sources.map((source) => {
                if (source.type === "source-url") {
                  const meta = source.providerMetadata
                    ?.noxus as WebReferenceMetadata;
                  const quote = omitCitationQuotes ? "" : meta?.quote || "";
                  const imageUrl = meta?.image_url || "";

                  return (
                    <InlineCitationCarouselItem
                      key={source.url}
                      className="overflow-hidden"
                    >
                      <InlineCitationSource
                        title={quote}
                        url={source.url}
                        imageUrl={imageUrl}
                      />
                    </InlineCitationCarouselItem>
                  );
                }

                const docMeta = source.providerMetadata?.noxus as
                  | DocumentReferenceMetadata
                  | undefined;
                const documentId = docMeta?.file_id;
                const quote = omitCitationQuotes
                  ? ""
                  : docMeta?.quote || source.title || "";

                const url =
                  docMeta?.url ||
                  `/api/backend/file/${documentId}?preview=true`;

                let displayText = source.filename || "Document";
                if (displayText.includes("%")) {
                  try {
                    displayText = decodeURIComponent(displayText);
                  } catch {
                    // Keep original if decoding fails
                  }
                }

                return (
                  <InlineCitationCarouselItem
                    key={source.sourceId}
                    className="overflow-hidden"
                  >
                    <InlineCitationSource
                      title={quote}
                      url={url}
                      urlText={displayText}
                    />
                  </InlineCitationCarouselItem>
                );
              })}
            </InlineCitationCarouselContent>
          </InlineCitationCarousel>
        </InlineCitationCardBody>
      </InlineCitationCard>
    </InlineCitation>
  );
}

// ---------------------------------------------------------------------------
// Pre-processing: merge text + source-group parts into text with markers
// ---------------------------------------------------------------------------

/**
 * Merge consecutive text → source-group pairs: append %%CITE:N%% markers
 * into the text content and collect the source-groups into a lookup array.
 *
 * Returns the modified parts (source-groups consumed by a preceding text are removed)
 * and the citation sources lookup.
 */
export function preprocessCitationParts(
  parts: GroupedPart[],
  citationStyle: string
): {
  processedParts: GroupedPart[];
  citationSources: (SourceUrlUIPart | SourceDocumentUIPart)[][];
} {
  if (citationStyle === "hidden") {
    return { processedParts: parts, citationSources: [] };
  }

  const processedParts: GroupedPart[] = [];
  const citationSources: (SourceUrlUIPart | SourceDocumentUIPart)[][] = [];

  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    const nextPart = parts[i + 1];

    if (part.type === "text" && nextPart?.type === "source-group") {
      const citeIndex = citationSources.length;
      citationSources.push(nextPart.parts);

      // Build the merged text: current text + marker, then keep consuming
      // any further text → source-group pairs so everything stays in one
      // Streamdown render (one <div>) instead of fragmenting into many.
      let mergedText = part.text + citationMarker(citeIndex);
      i++; // skip the source-group we just consumed

      while (i + 1 < parts.length) {
        const peek = parts[i + 1];
        if (peek?.type === "text") {
          const peekNext = parts[i + 2];
          if (peekNext?.type === "source-group") {
            // text → source-group: append both
            const ci = citationSources.length;
            citationSources.push(peekNext.parts);
            mergedText += peek.text + citationMarker(ci);
            i += 2; // skip text + source-group
            continue;
          }
          // text without a following source-group: merge the text but stop
          // consuming source-groups (the next iteration will handle it)
          mergedText += peek.text;
          i += 1;
          continue;
        }
        break;
      }

      processedParts.push({ ...part, text: mergedText });
      continue;
    }

    processedParts.push(part);
  }

  return { processedParts, citationSources };
}
